import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

const blue = "#003366";
const lightBlue = "#0056A8";
const cyan = "#0e7490";
const amber = "#d97706";
const gray = "#64748b";
const lightGray = "#f1f5f9";
const white = "#ffffff";

const s = StyleSheet.create({
  page: { padding: 40, fontFamily: "Helvetica", fontSize: 10, color: "#1e293b" },
  // Header
  headerBar: { backgroundColor: blue, padding: 20, marginBottom: 20, borderRadius: 4 },
  headerTitle: { fontSize: 20, fontFamily: "Helvetica-Bold", color: white, marginBottom: 4 },
  headerSub: { fontSize: 10, color: "#93c5fd" },
  headerBadge: { flexDirection: "row", gap: 10, marginTop: 8 },
  badge: { backgroundColor: "rgba(255,255,255,0.15)", paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  badgeText: { fontSize: 8, color: white, fontFamily: "Helvetica-Bold" },
  // Stats
  statsRow: { flexDirection: "row", gap: 10, marginBottom: 20 },
  statCard: { flex: 1, borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 4, padding: 10, alignItems: "center" },
  statValue: { fontSize: 22, fontFamily: "Helvetica-Bold", color: blue },
  statLabel: { fontSize: 7, color: gray, marginTop: 2, textAlign: "center" },
  statSub: { fontSize: 6, color: "#94a3b8", marginTop: 1 },
  // Section
  sectionTitle: { fontSize: 14, fontFamily: "Helvetica-Bold", color: blue, marginBottom: 8, marginTop: 15 },
  sectionDesc: { fontSize: 9, color: gray, marginBottom: 10 },
  // Table
  table: { marginBottom: 15 },
  tableHeader: { flexDirection: "row", backgroundColor: blue, paddingVertical: 6, paddingHorizontal: 8, borderRadius: 2 },
  tableHeaderText: { fontSize: 8, fontFamily: "Helvetica-Bold", color: white },
  tableRow: { flexDirection: "row", paddingVertical: 6, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#e2e8f0" },
  tableRowAlt: { flexDirection: "row", paddingVertical: 6, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#e2e8f0", backgroundColor: lightGray },
  tableCell: { fontSize: 9, color: "#1e293b" },
  tableCellBold: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#1e293b" },
  tableCellRight: { fontSize: 9, fontFamily: "Helvetica-Bold", color: blue, textAlign: "right" },
  tableFooter: { flexDirection: "row", paddingVertical: 6, paddingHorizontal: 8, borderTopWidth: 1, borderTopColor: blue },
  // Region
  regionRow: { flexDirection: "row", gap: 10, marginBottom: 15 },
  regionCard: { flex: 1, borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 4, padding: 12 },
  regionValue: { fontSize: 18, fontFamily: "Helvetica-Bold", color: blue },
  regionLabel: { fontSize: 8, color: gray, marginTop: 2 },
  // Capabilities
  capBox: { backgroundColor: blue, padding: 18, borderRadius: 4, marginTop: 15, marginBottom: 15 },
  capTitle: { fontSize: 14, fontFamily: "Helvetica-Bold", color: white, marginBottom: 8 },
  capText: { fontSize: 9, color: "#93c5fd", lineHeight: 1.5, marginBottom: 8 },
  capGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  capItem: { flexDirection: "row", alignItems: "center", gap: 4, width: "48%" },
  capCheck: { fontSize: 9, color: "#67e8f9" },
  capItemText: { fontSize: 8, color: "#dbeafe" },
  // Footer
  footer: { borderTopWidth: 0.5, borderTopColor: "#e2e8f0", paddingTop: 12, marginTop: 20, alignItems: "center" },
  footerText: { fontSize: 7, color: "#94a3b8", textAlign: "center" },
  footerContact: { fontSize: 8, color: gray, textAlign: "center", marginTop: 3 },
  // Page 2 growth
  growthRow: { flexDirection: "row", gap: 8, marginBottom: 6 },
  growthYear: { width: 50, fontSize: 10, fontFamily: "Helvetica-Bold", color: blue },
  growthBarBg: { flex: 1, height: 22, backgroundColor: lightGray, borderRadius: 3, overflow: "hidden", justifyContent: "center" },
  growthBarFill: { height: "100%", borderRadius: 3, justifyContent: "center", paddingLeft: 6 },
  growthBarText: { fontSize: 8, color: white, fontFamily: "Helvetica-Bold" },
  growthValue: { width: 65, fontSize: 9, fontFamily: "Helvetica-Bold", color: blue, textAlign: "right", alignSelf: "center" },
});

const UNITS_PER_CASE = 200;

const SALES_BY_SKU = [
  { size: '½" × ½"', string: "Locator String", cases: 82 },
  { size: '½" × 3"', string: "Locator String", cases: 91 },
  { size: '¼" × 6"', string: "No String", cases: 30 },
  { size: '½" × 6"', string: "No String", cases: 34 },
  { size: '¾" × 6"', string: "No String", cases: 38 },
  { size: '1" × 6"', string: "No String", cases: 30 },
];
const TOTAL_CASES = 305;
const TOTAL_UNITS = TOTAL_CASES * UNITS_PER_CASE;

const YOY = [
  { year: "2020", cases: 45 },
  { year: "2021", cases: 95 },
  { year: "2022", cases: 145 },
  { year: "2023", cases: 195 },
  { year: "2024", cases: 250 },
  { year: "2025", cases: 305 },
];
const MAX_CASES = 305;

export default function NeuroSpongePDF() {
  return (
    <Document>
      {/* PAGE 1 — Overview & Product Catalog */}
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.headerBar}>
          <Text style={s.headerTitle}>Neurosurgical Sponge Patties</Text>
          <Text style={s.headerSub}>Sterile Mate LLC — Sales & Distribution Report | Third-Party Distribution</Text>
          <View style={s.headerBadge}>
            <View style={s.badge}><Text style={s.badgeText}>TMC Distributor Since 2020</Text></View>
            <View style={s.badge}><Text style={s.badgeText}>Largest Client: U.S. Government</Text></View>
          </View>
        </View>

        {/* Stats */}
        <View style={s.statsRow}>
          <View style={s.statCard}>
            <Text style={s.statValue}>{TOTAL_CASES}</Text>
            <Text style={s.statLabel}>Cases Sold (2025)</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statValue}>{TOTAL_UNITS.toLocaleString()}</Text>
            <Text style={s.statLabel}>Total Units Sold</Text>
            <Text style={s.statSub}>200 units/case</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statValue}>7</Text>
            <Text style={s.statLabel}>TX Health Systems</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statValue}>TX + LA</Text>
            <Text style={s.statLabel}>States Served</Text>
          </View>
          <View style={s.statCard}>
            <Text style={s.statValue}>578%</Text>
            <Text style={s.statLabel}>Growth Since 2020</Text>
          </View>
        </View>

        {/* Regional Breakdown */}
        <Text style={s.sectionTitle}>Sales by Region</Text>
        <View style={s.regionRow}>
          <View style={s.regionCard}>
            <Text style={s.regionValue}>265</Text>
            <Text style={s.regionLabel}>Cases — Texas Medical Center (7 Health Systems)</Text>
          </View>
          <View style={s.regionCard}>
            <Text style={s.regionValue}>40</Text>
            <Text style={s.regionLabel}>Cases — Louisiana</Text>
          </View>
          <View style={s.regionCard}>
            <Text style={{ ...s.regionValue, color: cyan }}>305</Text>
            <Text style={s.regionLabel}>Total Cases Sold (2025)</Text>
          </View>
        </View>

        {/* Product Catalog Table */}
        <Text style={s.sectionTitle}>Product Catalog & 2025 Sales</Text>
        <Text style={s.sectionDesc}>Neurosurgical sponge patties — all 6 sizes in continuous inventory</Text>

        <View style={s.table}>
          <View style={s.tableHeader}>
            <Text style={{ ...s.tableHeaderText, width: "25%" }}>Product Size</Text>
            <Text style={{ ...s.tableHeaderText, width: "25%" }}>String Type</Text>
            <Text style={{ ...s.tableHeaderText, width: "25%", textAlign: "right" }}>Cases Sold</Text>
            <Text style={{ ...s.tableHeaderText, width: "25%", textAlign: "right" }}>Total Units</Text>
          </View>
          {SALES_BY_SKU.map((item, idx) => (
            <View key={idx} style={idx % 2 === 1 ? s.tableRowAlt : s.tableRow}>
              <Text style={{ ...s.tableCellBold, width: "25%" }}>{item.size}</Text>
              <Text style={{ ...s.tableCell, width: "25%", color: item.string === "Locator String" ? cyan : amber }}>{item.string}</Text>
              <Text style={{ ...s.tableCellRight, width: "25%" }}>{item.cases}</Text>
              <Text style={{ ...s.tableCellRight, width: "25%" }}>{(item.cases * UNITS_PER_CASE).toLocaleString()}</Text>
            </View>
          ))}
          <View style={s.tableFooter}>
            <Text style={{ ...s.tableCellBold, width: "50%" }}>Grand Total</Text>
            <Text style={{ ...s.tableCellRight, width: "25%", color: cyan }}>{TOTAL_CASES}</Text>
            <Text style={{ ...s.tableCellRight, width: "25%", color: cyan }}>{TOTAL_UNITS.toLocaleString()}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerText}>STERILE MATE LLC — Neurosurgical Supply Distribution</Text>
          <Text style={s.footerContact}>800.988.3892 | 281.456.3458 | george.broughton@sterilemate.com | www.sterilemate.com | AskIsaac.io</Text>
          <Text style={{ ...s.footerText, marginTop: 4 }}>Page 1 of 2</Text>
        </View>
      </Page>

      {/* PAGE 2 — Growth & Capabilities */}
      <Page size="A4" style={s.page}>
        <View style={s.headerBar}>
          <Text style={{ ...s.headerTitle, fontSize: 16 }}>Annual Sales Growth & Distribution Capabilities</Text>
          <Text style={s.headerSub}>Sterile Mate LLC — 2020 to 2025</Text>
        </View>

        {/* YOY Bar Chart */}
        <Text style={s.sectionTitle}>Year-over-Year Sales (Cases)</Text>
        <Text style={s.sectionDesc}>Growth from 45 cases at founding (2020) to 305 cases in 2025</Text>

        <View style={{ marginBottom: 20 }}>
          {YOY.map((item) => (
            <View key={item.year} style={s.growthRow}>
              <Text style={s.growthYear}>{item.year}</Text>
              <View style={s.growthBarBg}>
                <View style={{ ...s.growthBarFill, width: `${(item.cases / MAX_CASES) * 100}%`, backgroundColor: item.year === "2025" ? cyan : lightBlue }}>
                  <Text style={s.growthBarText}>{item.cases} cases</Text>
                </View>
              </View>
              <Text style={s.growthValue}>{(item.cases * UNITS_PER_CASE).toLocaleString()} units</Text>
            </View>
          ))}
        </View>

        {/* Capabilities */}
        <View style={s.capBox}>
          <Text style={s.capTitle}>Why Health Systems Trust Sterile Mate</Text>
          <Text style={s.capText}>
            Since 2020, Sterile Mate has served as a dependable secondary source for neurosurgical sponge patties when primary distributors face backorders, allocation limits, or lead time delays. We maintain inventory across all 6 standard and specialty sizes, serving 7 healthcare systems in the Texas Medical Center and facilities in Louisiana. Our largest client is the U.S. Government.
          </Text>
          <View style={s.capGrid}>
            {[
              "Same-day and emergency fulfillment",
              "All 6 SKU sizes in continuous inventory",
              "Competitive third-party pricing",
              "7 TMC health systems + Louisiana",
              "U.S. Government contract supplier",
              "Direct delivery to surgical supply rooms",
              "No minimum order requirements",
              "200 units per case standard packaging",
            ].map((item, idx) => (
              <View key={idx} style={s.capItem}>
                <Text style={s.capCheck}>&#10003;</Text>
                <Text style={s.capItemText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Contact Block */}
        <View style={{ borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 4, padding: 15, marginTop: 15 }}>
          <Text style={{ fontSize: 12, fontFamily: "Helvetica-Bold", color: blue, marginBottom: 6 }}>Contact</Text>
          <Text style={{ fontSize: 10, fontFamily: "Helvetica-Bold", color: "#1e293b" }}>George Broughton III</Text>
          <Text style={{ fontSize: 9, color: gray }}>President & Strategic Director — Sterile Mate LLC</Text>
          <Text style={{ fontSize: 9, color: gray, marginTop: 4 }}>Serving Houston, Texas and Surrounding Communities</Text>
          <View style={{ flexDirection: "row", gap: 20, marginTop: 8 }}>
            <Text style={{ fontSize: 9, color: blue }}>Office: 800.988.3892</Text>
            <Text style={{ fontSize: 9, color: blue }}>Direct: 281.456.3458</Text>
            <Text style={{ fontSize: 9, color: blue }}>Mobile: 323.243.9139</Text>
          </View>
          <Text style={{ fontSize: 9, color: blue, marginTop: 4 }}>george.broughton@sterilemate.com</Text>
          <View style={{ flexDirection: "row", gap: 15, marginTop: 4 }}>
            <Text style={{ fontSize: 9, color: gray }}>www.sterilemate.com</Text>
            <Text style={{ fontSize: 9, color: gray }}>AskIsaac.io</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerText}>STERILE MATE LLC — Neurosurgical Supply Distribution</Text>
          <Text style={s.footerContact}>Serving Houston, Texas and Surrounding Communities</Text>
          <Text style={{ ...s.footerText, marginTop: 4 }}>Page 2 of 2</Text>
        </View>
      </Page>
    </Document>
  );
}
